import { FinanceRepository } from '@lib/api/repository/finance/finance.repository';
import {
  FinanceBalance,
  LedgerEntry,
  PayoutDestination,
  PayoutDestinationInput,
  Withdrawal,
  WithdrawalInput,
  WithdrawalQuote,
} from '@lib/types';
import { AdminPage, AdminPageFilters } from '@lib/types/admin-page';

import { Requester } from '../clients';

export default class FinanceAPI implements FinanceRepository {
  async listAdminPage(filters: AdminPageFilters, signal?: AbortSignal): Promise<AdminPage<Withdrawal>> {
    const result = (await Requester.get<AdminPage<Withdrawal>>('/marketplace/admin/withdrawals/page', { params: { ...filters }, signal })).data;

    return { items: result.items.map((item) => item), total: result.total };
  }

  async findWithdrawalByKey(key: string, signal?: AbortSignal) {
    return (await Requester.get<Withdrawal | null>(`/marketplace/finance/withdrawals/by-key/${encodeURIComponent(key)}`, { signal })).data;
  }

  async quoteWithdrawal(requestedCents: number) {
    return (await Requester.post<WithdrawalQuote>('/marketplace/finance/withdrawals/quote', { body: { requestedCents } })).data;
  }

  async getBalance(signal?: AbortSignal) {
    return (
      await Requester.get<FinanceBalance>('/marketplace/finance/balance', { signal })
    ).data;
  }

  async listLedger(signal?: AbortSignal) {
    return (
      await Requester.get<LedgerEntry[]>('/marketplace/finance/ledger', { signal })
    ).data;
  }

  async listWithdrawals(signal?: AbortSignal) {
    return (
      await Requester.get<Withdrawal[]>('/marketplace/finance/withdrawals', { signal })
    ).data;
  }

  async requestWithdrawal(input: WithdrawalInput) {
    return (
      await Requester.post<Withdrawal>('/marketplace/finance/withdrawals', {
        body: input,
      })
    ).data;
  }

  async listPayoutDestinations(signal?: AbortSignal) {
    return (
      await Requester.get<PayoutDestination[]>(
        '/marketplace/finance/payout-destinations', { signal },
      )
    ).data;
  }

  async createPayoutDestination(input: PayoutDestinationInput) {
    return (
      await Requester.post<PayoutDestination>(
        '/marketplace/finance/payout-destinations',
        { body: input },
      )
    ).data;
  }

  async setDefaultPayoutDestination(destinationId: string) {
    return (
      await Requester.patch<PayoutDestination>(
        `/marketplace/finance/payout-destinations/${destinationId}/default`,
      )
    ).data;
  }

  async deactivatePayoutDestination(destinationId: string) {
    await Requester.delete(
      `/marketplace/finance/payout-destinations/${destinationId}`,
    );
  }

  async listAdminWithdrawals(signal?: AbortSignal) {
    return (
      await Requester.get<Withdrawal[]>('/marketplace/admin/withdrawals', { signal })
    ).data;
  }

  async reviewWithdrawal(
    withdrawalId: string,
    approved: boolean,
    note?: string,
  ) {
    const action = approved ? 'approve' : 'reject';

    return (
      await Requester.post<Withdrawal>(
        `/marketplace/admin/withdrawals/${withdrawalId}/${action}`,
        { body: { note } },
      )
    ).data;
  }

  async finalizeWithdrawal(withdrawalId: string, note?: string) {
    return (
      await Requester.post<Withdrawal>(
        `/marketplace/admin/withdrawals/${withdrawalId}/finalize`,
        { body: { note } },
      )
    ).data;
  }
}

